import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, ElementRef, inject, OnInit, signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CreateMutationResult, CreateQueryResult } from '@tanstack/angular-query-experimental';
import { MenuItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { PermissionCheckConfig } from '@/common/directives/permission-check.directive';
import { Media } from '@/common/models';
import { DefinedPermission, PERMISSIONS } from '@/common/models/permission.model';
import { MessageService, PermissionService } from '@/common/services';
import { TutorialService } from '@/common/services/tutorial.service';
import { LayoutService } from '@/layout/services/layout.service';
import { TranslationService } from '@/modules/i18n';
import { User } from '@/pages/auth/models';
import { AuthService } from '@/pages/auth/services/auth.service';
import { Offer, OfferSortField } from '@/pages/offers/models/offers.model';
import { OffersHttpService } from '@/pages/offers/services/http/offers-http.service';
import { AiChatType } from '@/pages/tiktok/models/tiktok-ui.model';
import { environment } from '@environments/environment';
import appVersion from '../../../../../app_version.json';

interface NavbarItem {
  id: string;
  label: string;
  routerLink: string[];
  active?: boolean;
  icon: string;
  badge?: boolean;
  permissions?: DefinedPermission[];
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
  private readonly _translationService: TranslationService = inject(TranslationService);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _translate: TranslateService = inject(TranslateService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _permissionService: PermissionService = inject(PermissionService);
  private readonly _router: Router = inject(Router);
  private readonly _tutorialService: TutorialService = inject(TutorialService);
  private readonly _offersHttpService: OffersHttpService = inject(OffersHttpService);
  private _customTitleActive = false;

  protected readonly layoutService: LayoutService = inject(LayoutService);

  version = 'v' + appVersion.version + '-' + environment.appEnv;
  profileMenuItems: MenuItem[];
  navbarItems: NavbarItem[];
  title: string;
  subtitle: string | null = null;

  @ViewChild('menuButton') menuButton!: ElementRef;
  @ViewChild('navbarMenuButton') navbarMenuButton!: ElementRef;
  @ViewChild('navbarMenu') menu!: ElementRef;

  readonly sidebarMenuVisible: WritableSignal<boolean> = signal<boolean>(false);
  readonly panelMenuVisible: WritableSignal<boolean> = signal<boolean>(false);
  readonly aiChatVisible: WritableSignal<boolean> = signal<boolean>(false);
  readonly activeAiType: WritableSignal<AiChatType> = signal<AiChatType>('wokapIA');
  readonly enableWokapIA = environment.enableWokapIA;
  readonly enableNavigation = environment.enableNavigation;
  readonly hasOffers: WritableSignal<boolean> = signal<boolean>(false);
  readonly hasCandidates: WritableSignal<boolean> = signal<boolean>(false);

  readonly companyMedia: WritableSignal<Media | null | undefined> = signal<Media | null | undefined>(null);
  readonly companyName: WritableSignal<string> = signal<string>('');
  readonly currentRoute: WritableSignal<string> = signal<string>('');
  readonly userAvatar: WritableSignal<Media | null | undefined> = signal<Media | null | undefined>(null);

  queryProfile: CreateQueryResult<User, HttpErrorResponse> = this._authService.getProfile();
  readonly updateUserProfile: CreateMutationResult<Partial<User>, HttpErrorResponse, Partial<User>> = this._authService.updateUserProfile();

  constructor() {
    // Mostrar p-panelMenu con delay al abrir (evitar glitch visual con animación del sidebar)
    // y destruirlo inmediatamente al cerrar para resetear su estado interno
    effect(() => {
      if (this.sidebarMenuVisible()) {
        setTimeout(() => this.panelMenuVisible.set(true), 300);
        this.checkOffersAndCandidates();
      } else {
        this.panelMenuVisible.set(false);
        // Colapsar sub-menús expandidos (idioma, guías) al cerrar el sidebar
        this.profileMenuItems?.forEach(item => {
          if (item.expanded) {
            item.expanded = false;
          }
        });
      }
    }, { allowSignalWrites: true });

    effect(() => {
      // efecto para que cuando se recargue el profile vuelva a llamar al ngOnInit y actualice los datos
      if (this.queryProfile.data()) {
        // quiero del objeto de this.navbarItems actualizar el objeto con id = chat y actualizar su propiedad badge
        const chatItem = this.navbarItems?.find(item => item.id === 'chat');
        if (chatItem) {
          chatItem.badge = this.queryProfile.data()?.hasPendingMessages ?? false;
        }
        this.userAvatar.set(this.queryProfile.data()?.avatar);

        this.companyMedia.set(this.queryProfile.data()?.entity.media);
        if (this.isPlatformUser()) {
          this.companyName.set('wokap');
        } else {
          const entity = this.queryProfile.data()?.entity;
          this.companyName.set(entity?.businessName ?? entity?.name ?? '');
        }
      }
    }, { allowSignalWrites: true });

    // Escuchar solicitud de inicio de tour desde LayoutService
    effect(() => {
      if (this.layoutService.startTourRequested()) {
        untracked(() => {
          this.layoutService.clearTourRequest();
          this.launchTutorial();
        });
      }
    });

    // Escuchar solicitud de inicio del tour de ofertas
    effect(() => {
      if (this.layoutService.startOffersTourRequested()) {
        untracked(() => {
          this.layoutService.clearOffersTourRequest();
          this._tutorialService.startOffersTour(
            () => {
              this.sidebarMenuVisible.set(false);
              // Siempre mostrar el dialog "Tour finalizado" al terminar el tour de ofertas.
              // Si hay candidatos, se lanzará el tour de candidatos al cerrar el dialog.
                firstValueFrom(this._offersHttpService.fetch({ limit: 50, offset: 0, sortField: OfferSortField.DATE, sortOrder: 'desc' })).then(response => {
                const offerWithCandidates = response.results.find((offer: Offer) => offer.candidates.TOTAL > 0);
                if (offerWithCandidates) {
                  this.layoutService.pendingOffersTourAfterFinished.set(false);
                  this.layoutService.tourFinishedShowCandidatesHint.set(false);
                  this.layoutService.pendingCandidatesTourOfferId.set(offerWithCandidates.id);
                } else {
                  this.layoutService.tourInProgress.set(false);
                  this.layoutService.tourFinishedShowCandidatesHint.set(true);
                }
                this.layoutService.showTourFinishedDialog.set(true);
              }).catch(() => {
                this.layoutService.tourInProgress.set(false);
                this.layoutService.tourFinishedShowCandidatesHint.set(false);
                this.layoutService.showTourFinishedDialog.set(true);
              });
            },
          );
        });
      }
    });
  }

  ngOnInit(): void {
    this.companyMedia.set(this._authService.getUser()?.entity.media);
    if (this.isPlatformUser()) {
      this.companyName.set('wokap');
    } else {
      const entity = this._authService.getUser()?.entity;
      this.companyName.set(entity?.businessName ?? entity?.name ?? '');
    }
    this.userAvatar.set(this._authService.getUser()?.avatar);
    const allNavbarItems: NavbarItem[] = [
      {
        id: 'dashboard',
        label: 'DASHBOARD.TITLE',
        routerLink: ['/dashboard'],
        icon: 'pi pi-home',
        permissions: [PERMISSIONS.ENTITY.VIEW_ALL, PERMISSIONS.USER.VIEW_ALL],
      },
      {
        id: 'individual-user-management',
        label: 'LAYOUT.NAVBAR.APP_USERS',
        routerLink: ['/individual-user-management'],
        icon: 'pi pi-users',
        permissions: [PERMISSIONS.USER.VIEW_ALL],
      },
      {
        id: 'companies',
        label: 'COMPANIES.LIST.TITLE',
        routerLink: ['/company/companies'],
        icon: 'pi pi-briefcase',
        permissions: [PERMISSIONS.ENTITY.VIEW_ALL, PERMISSIONS.ENTITY.VIEW],
      },
      {
        id: 'saas-training',
        label: 'LAYOUT.NAVBAR.SAAS_TRAINING',
        routerLink: ['/saas-training'],
        icon: 'pi pi-graduation-cap',
        permissions: [PERMISSIONS.USER.VIEW_ALL],
      },
      {
        id: 'offers', // <-- Item de cliente
        label: 'OFFERS.TITLE',
        routerLink: ['/offers'],
        icon: 'pi pi-list',
        permissions: [PERMISSIONS.OFFER.VIEW],
      },
      {
        id: 'chat', // <-- Item de cliente
        label: 'CHAT.TITLE',
        routerLink: ['/chat'],
        icon: 'pi pi-comments',
        badge: this.queryProfile.data()?.hasPendingMessages ?? false,
        permissions: [PERMISSIONS.APPLY_OFFER.VIEW],
      },
      {
        id: 'add_offer', // <-- Item de cliente
        label: 'OFFERS.NEW_OFFER',
        routerLink: ['/offers/edit/create'],
        icon: 'pi pi-plus',
        permissions: [PERMISSIONS.OFFER.VIEW, PERMISSIONS.OFFER.CREATE, PERMISSIONS.OFFER.EDIT],
      },
    ];

    // 2. AHORA, filtramos la lista ANTES de asignarla a this.navbarItems
    if (this.isPlatformUser()) {
      // Si es un usuario de plataforma, excluimos los items de cliente
      const clientOnlyIds = ['offers', 'chat', 'add_offer'];
      this.navbarItems = allNavbarItems.filter(item => !clientOnlyIds.includes(item.id));
    } else {
      // Si es un usuario normal (cliente), le asignamos todos los items
      this.navbarItems = allNavbarItems;
    }

    // Creamos los elementos del menú de forma inicial
    this.profileMenuItems = [
      {
        id: 'company',
        label: 'LAYOUT.NAVBAR.COMPANY',
        icon: 'pi pi-briefcase',
        command: () => {
          // Ref: docs/informe/PLAN_IMPLEMENTACION_PERMISOS.md - Fase 2.1
          // Verificar permisos antes de navegar a edición de empresa
          this._permissionService.checkPermissions([PERMISSIONS.ENTITY.VIEW]).subscribe(hasPermission => {
            if (!hasPermission) {
              this._messageService.add({
                severity: 'error',
                summary: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE',
                detail: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE',
              });

              return;
            }

            this._router.navigate(['/company/edit/' + this._authService.getUser()?.entity.id]);
            this.sidebarMenuVisible.set(false);
          });
        },
        visible: true, // Visible sin restricción de permisos
      },
      {
        id: 'data-management',
        label: 'LAYOUT.NAVBAR.DATA_MANAGEMENT',
        icon: 'pi pi-database',
        command: () => {
          // Ref: docs/informe/PLAN_IMPLEMENTACION_PERMISOS.md - Fase 2.1
          // Verificar permisos antes de navegar a gestión de datos
          this._permissionService.checkPermissions([PERMISSIONS.ENTITY.VIEW]).subscribe(hasPermission => {
            if (!hasPermission) {
              this._messageService.add({
                severity: 'error',
                summary: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE',
                detail: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE',
              });

              return;
            }

            this._router.navigate(['/company/data-management']);
            this.sidebarMenuVisible.set(false);
          });
        },
        visible: true, // Visible sin restricción de permisos
      },
      {
        id: 'user-management',
        label: 'LAYOUT.NAVBAR.USER-MANAGEMENT',
        icon: 'pi pi-users',
        command: () => {
          // Ref: docs/informe/PLAN_IMPLEMENTACION_PERMISOS.md - Fase 2.1
          // Verificar permisos antes de navegar a gestión de usuarios
          this._permissionService.checkPermissions([PERMISSIONS.USER.VIEW]).subscribe(hasPermission => {
            if (!hasPermission) {
              this._messageService.add({
                severity: 'error',
                summary: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE',
                detail: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE',
              });

              return;
            }

            this._router.navigate(['/user-management']);
            this.sidebarMenuVisible.set(false);
          });
        },
        visible: true, // Visible sin restricción de permisos
      },
      {
        id: 'entity-location',
        label: 'LAYOUT.NAVBAR.ENTITY-LOCATION',
        icon: 'pi pi-map-marker',
        command: () => {
          this._permissionService.checkPermissions([PERMISSIONS.ENTITY_LOCATION.VIEW]).subscribe(hasPermission => {
            if (!hasPermission) {
              this._messageService.add({
                severity: 'error',
                summary: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE',
                detail: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE',
              });

              return;
            }

            this._router.navigate(['/entity-location']);
            this.sidebarMenuVisible.set(false);
          });
        },
        visible: true,
      },
      {
        id: 'user-action-log',
        label: 'LAYOUT.NAVBAR.USER-ACTION-LOG',
        icon: 'pi pi-history',
        command: () => {
          this._permissionService.checkPermissions([PERMISSIONS.USER_ACTION_LOG.VIEW_ALL]).subscribe(hasPermission => {
            if (!hasPermission) {
              this._messageService.add({
                severity: 'error',
                summary: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE',
                detail: 'COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE',
              });

              return;
            }

            this._router.navigate(['/user-action-log']);
            this.sidebarMenuVisible.set(false);
          });
        },
        visible: true,
      },
      {
        id: 'ai-connector',
        label: 'LAYOUT.NAVBAR.AI_CONNECTOR',
        icon: 'pi pi-microchip-ai',
        command: () => {
          this._router.navigate(['/ai-connector']);
          this.sidebarMenuVisible.set(false);
        },
        visible: true, // Visible sin restricción de permisos
      },
      {
        id: 'language',
        label: 'LAYOUT.NAVBAR.LANGUAGE',
        icon: 'pi pi-language',
        items: [
          {
            label: 'LAYOUT.NAVBAR.LANGUAGES.EN',
            flag: '/assets/images/icons/flag/uk-flag.svg',
            command: () => {
              this._translationService.setLanguage('en').subscribe();
              this.updateLocale('en');
              this.sidebarMenuVisible.set(false);
            },
          },
          {
            label: 'LAYOUT.NAVBAR.LANGUAGES.ES',
            flag: '/assets/images/icons/flag/spain-flag.svg',
            command: () => {
              this._translationService.setLanguage('es').subscribe();
              this.updateLocale('es');
              this.sidebarMenuVisible.set(false);
            },
          },
        ],
      },
      {
        id: 'contact',
        label: 'LAYOUT.NAVBAR.CONTACT',
        flag: '/assets/images/auriculares.png',
        items: [
          {
            label: 'WhatsApp',
            icon: 'pi pi-whatsapp',
            command: () => {
              window.open('https://wa.me/' + environment.whatsappPhone, '_blank');
              this.sidebarMenuVisible.set(false);
            },
          },
          {
            label: 'Email',
            icon: 'pi pi-envelope',
            command: () => {
              window.location.href = 'mailto:saas@wokap.com';
              this.sidebarMenuVisible.set(false);
            },
          },
        ],
      },
      {
        id: 'tutorial',
        label: 'LAYOUT.NAVBAR.TUTORIAL',
        icon: 'pi pi-question-circle',
        items: this.buildTutorialItems(),
      },
    ];

    this.companyMedia.set(this._authService.getUser()?.entity.media);
    const entityFinal = this._authService.getUser()?.entity;
    this.companyName.set(entityFinal?.businessName ?? entityFinal?.name ?? '');

    // Subscribe to route changes
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute.set(event.urlAfterRedirects);
        this.updateActiveNavbarItems();
      }
    });

    this._translate.onLangChange.subscribe(() => {
      this.updateActiveNavbarItems();
    });

    // Initial calls
    this.updateActiveNavbarItems();
    this.currentRoute.set(this._router.url);
    this.checkOffersAndCandidates();

    // nos suscribimos a los cambios de traducciones para poder recargar el título y otros textos
    this._translate.onLangChange.subscribe(() => {
      this.updateActiveNavbarItems();
      this.collectRouteData(this._activatedRoute);
    });

    // Escuchar eventos personalizados para actualizar el título desde componentes externos (ej: TikTok)
    window.addEventListener('updateNavbarTitle', (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        if (customEvent.detail.title !== undefined) {
          this.title = customEvent.detail.title;
          // Activar la bandera si el título es válido, desactivarla si es null
          this._customTitleActive = customEvent.detail.title !== null;
        }
        if (customEvent.detail.subtitle !== undefined) {
          this.subtitle = customEvent.detail.subtitle;
        }
      }
    });
  }

  logout(): void {
    this._authService.logout();
  }

  launchTutorial(): void {
    // Marcar inmediatamente que un tour está en progreso
    this.layoutService.tourInProgress.set(true);
    // Bloquear el modal de bienvenida mientras el tour esté activo
    this.layoutService.firstOfferModalAllowed.set(false);
    // Abrir sidebar primero para que los items del p-panelMenu estén en el DOM
    this.sidebarMenuVisible.set(true);
    setTimeout(() => {
      this._tutorialService.startTour(
        // onFinished: cerrar sidebar al acabar
        () => {
          this.sidebarMenuVisible.set(false);
          // Si hay ofertas, mostrar tour-finished y luego lanzar tour de ofertas; si no, solo tour-finished
          firstValueFrom(this._offersHttpService.fetch({ limit: 1, offset: 0 })).then(response => {
            if (response.total > 0) {
              this.layoutService.pendingOffersTourAfterFinished.set(true);
              this.layoutService.showTourFinishedDialog.set(true);
            } else {
              this.layoutService.tourInProgress.set(false);
              this.layoutService.firstOfferModalAllowed.set(true);
              this.layoutService.showTourFinishedDialog.set(true);
            }
          }).catch(() => {
            this.layoutService.tourInProgress.set(false);
            this.layoutService.firstOfferModalAllowed.set(true);
            this.layoutService.showTourFinishedDialog.set(true);
          });
        },
        // closeSidebar: cerrar sidebar cuando llegue a elementos de la navbar superior
        () => { this.sidebarMenuVisible.set(false); },
      );
    }, 450);
  }

  launchOfferTour(): void {
    const currentUrl = this._router.url;
    const isOnOffers = currentUrl.startsWith('/offers') && !currentUrl.includes('/offers/');

    this.layoutService.tourInProgress.set(true);
    this.layoutService.firstOfferModalAllowed.set(false);
    // Lanzamiento manual desde el menú: no es parte de una cadena de guías
    this.layoutService.chainedTourActive.set(false);

    if (isOnOffers) {
      this.layoutService.offersTutorialDontShowAgainValue.set(false);
      this.layoutService.showOffersTutorialDialog.set(true);
    } else {
      this._router.navigate(['/offers']).then(() => {
        setTimeout(() => {
          this.layoutService.offersTutorialDontShowAgainValue.set(false);
          this.layoutService.showOffersTutorialDialog.set(true);
        }, 500);
      });
    }
  }

  launchCandidatesTour(): void {
    // Lanzamiento manual desde el menú: no es parte de una cadena de guías
    this.layoutService.chainedTourActive.set(false);
    const currentUrl = this._router.url.split('?')[0]; // Quitar query params
    const offerDetailMatch = currentUrl.match(/^\/offers\/(\d+)/);

    if (offerDetailMatch && !currentUrl.includes('/edit/')) {
      // Ya estamos en detalle de una oferta, lanzar tour aquí mismo
      localStorage.setItem('preferredView', 'list');
      setTimeout(() => {
        this.layoutService.startCandidatesTourRequested.set(true);
      }, 300);
    } else {
      // No estamos en detalle de oferta, buscar la más reciente con candidatos
      firstValueFrom(this._offersHttpService.fetch({ limit: 50, offset: 0, sortField: OfferSortField.DATE, sortOrder: 'desc' })).then(response => {
        const offerWithCandidates = response.results.find((offer: Offer) => offer.candidates.TOTAL > 0);
        if (offerWithCandidates) {
          localStorage.setItem('preferredView', 'list');
          this._router.navigate(['/offers', offerWithCandidates.id]).then(() => {
            setTimeout(() => {
              this.layoutService.startCandidatesTourRequested.set(true);
            }, 500);
          });
        } else {
          this._messageService.add({
            severity: 'info',
            summary: 'Info',
            detail: 'LAYOUT.NAVBAR.TUTORIALS.NO_CANDIDATES_MESSAGE',
          });
        }
      });
    }
  }

  checkOffersAndCandidates(): void {
    firstValueFrom(this._offersHttpService.fetch({ limit: 50, offset: 0, sortField: OfferSortField.DATE, sortOrder: 'desc' })).then(response => {
      this.hasOffers.set(response.total > 0);
      const offerWithCandidates = response.results.find((offer: Offer) => offer.candidates.TOTAL > 0);
      this.hasCandidates.set(!!offerWithCandidates);
      this.updateTutorialMenuItems();
    }).catch(() => {
      this.hasOffers.set(false);
      this.hasCandidates.set(false);
      this.updateTutorialMenuItems();
    });
  }

  closeSidebarMenu(): void {
    this.sidebarMenuVisible.set(false);
  }

  updateActiveNavbarItems(): void {
    this.collectRouteData(this._activatedRoute);
  }

  collectRouteData(route: ActivatedRoute): void {
    route.data.subscribe(data => {
      this.navbarItems?.forEach(item => {
        // Si es la ruta edit/create, marcar como activo solo el elemento add_offer
        if (data['path'] === 'edit/create') {
          item.active = item.id === 'add_offer';
        } else {
          // Para el resto de casos, usar navbarItem
          item.active = data['navbarItem'] === item.id;
        }
      });

      // Solo actualizar el título si no hay un título personalizado activo
      if (data['title'] && !this._customTitleActive) {
        this.title = this._translate.instant(data['title']);
      }

      if (route.firstChild) {
        this.collectRouteData(route.firstChild);
      }
    });
  }

  getOfferActionConfig(permissions: DefinedPermission[], routerLink: string[]): PermissionCheckConfig {
    return {
      permissions,
      action: () => {
        // For create offer button: if already on edit/create, navigate away and back to force reset
        if (routerLink.length === 1 && routerLink[0] === '/offers/edit/create') {
          const currentUrl = this._router.url;
          if (currentUrl.startsWith('/offers/edit/create')) {
            this._router.navigate(['/offers']).then(() => {
              this._router.navigate(['/offers/edit/create']);
            }).catch(() => {});
          } else {
            this.navigate(routerLink);
          }
        } else {
          this.navigate(routerLink);
        }
      },
    };
  }

  navigate(routerLink: string[]): void {
    this._router.navigate(routerLink).catch(() => {
      console.error('Navigation failed in navbar.component/navigate');
    });
  }

  /** Navega a la ruta principal según el tipo de usuario (logo / nombre "wokap") */
  navigateHome(): void {
    this.navigate(this._authService.getDefaultRoute());
  }

  navigateToProfile(): void {
    this._router.navigate(['/auth/profile']);
  }

  getUsername(): string {
    const user = this._authService.getUser();

    return user?.firstname ?? '';
  }

  onSidebarMenuToggle(): void {
    this.sidebarMenuVisible.update(prev => !prev);
  }

  updateLocale(locale: string): void {
    const currentUser = this._authService.getUser();

    this.updateUserProfile.mutate({
      firstname: currentUser!.firstname,
      lastname: currentUser!.lastname,
      email: currentUser!.email,
      locale,
    } as User, {
      onSuccess: () => {
        // document.location.reload();
      },
      onError: (error: HttpErrorResponse) => {
        console.error('Error on NavbarComponent/onChangeLocale: ', error);
        this._messageService.add({
          severity: 'error',
          summary: 'COMMON.ERRORS.TITLE',
          detail: 'COMMON.ERRORS.GENERIC',
        });
      },
    });
  }

  getUserInitials(): string {
    const user = this._authService.getUser();
    let initials = '';

    if (user?.firstname) {
      initials += user.firstname.charAt(0);
    }
    if (user?.lastname) {
      initials += user.lastname.charAt(0);
    }

    // Si no hay nombre pero sí email, usamos la inicial del email
    if (!initials && user?.email) {
      initials += user.email.charAt(0);
    }

    return initials.toUpperCase();
  }

  isPlatformUser(): boolean {
    const user = this._authService.getUser();

    return user ? user.group.name.includes('platform') : false;
  }

  isNavigationActive(): boolean {
    return this.currentRoute().startsWith('/navigation');
  }

  isWokapIaActive(): boolean {
    return this.currentRoute().startsWith('/wokap-ia');
  }

  /**
   * Navega a la vista wokapIA (/wokap-ia).
   * Si ya está en /wokap-ia, navega a otra ruta primero para activar
   * el guard de formularios sucios, y luego regresa para reiniciar el wizard.
   */
  openAiChat(): void {
    const currentUrl = this._router.url;
    if (currentUrl.includes('/wokap-ia')) {
      this._router.navigate(['/offers']).then(() => {
        this._router.navigate(['/wokap-ia']);
      });
    } else {
      this._router.navigate(['/wokap-ia']);
    }
  }

  private buildTutorialItems(): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: 'General',
        icon: 'pi pi-circle-fill tutorial-bullet-icon',
        command: () => {
          this.sidebarMenuVisible.set(false);
          this.layoutService.tutorialDontShowAgainValue.set(false);
          this.layoutService.showTutorialDialog.set(true);
        },
      },
    ];

    if (this.hasOffers()) {
      items.push({
        label: 'Ofertas',
        icon: 'pi pi-circle-fill tutorial-bullet-icon',
        command: () => {
          this.sidebarMenuVisible.set(false);
          this.launchOfferTour();
        },
      });
    } else {
      items.push({
        label: 'Ofertas',
        icon: 'pi pi-circle-fill tutorial-bullet-icon',
        state: { tooltipKey: 'LAYOUT.NAVBAR.TUTORIALS.OFFERS_DISABLED_TOOLTIP' },
        disabled: true,
        styleClass: 'tutorial-item-disabled',
      });
    }

    if (this.hasCandidates()) {
      items.push({
        label: 'Candidat@s',
        icon: 'pi pi-circle-fill tutorial-bullet-icon',
        command: () => {
          this.sidebarMenuVisible.set(false);
          this.launchCandidatesTour();
        },
      });
    } else {
      items.push({
        label: 'Candidat@s',
        icon: 'pi pi-circle-fill tutorial-bullet-icon',
        state: { tooltipKey: 'LAYOUT.NAVBAR.TUTORIALS.CANDIDATES_DISABLED_TOOLTIP' },
        disabled: true,
        styleClass: 'tutorial-item-disabled',
      });
    }

    return items;
  }

  private updateTutorialMenuItems(): void {
    const tutorialItem = this.profileMenuItems?.find(item => item.id === 'tutorial');
    if (tutorialItem) {
      tutorialItem.items = this.buildTutorialItems();
    }
  }
}
